import {
  Drawer,
  Table,
  Button,
  Space,
  Tag,
  Switch,
  Tooltip,
  Popconfirm,
  Typography,
  Empty,
  Descriptions,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";

const { Text } = Typography;

const formatCost = (val) =>
  val == null ? "-" : `$${Number(val).toFixed(2)}`;

const ProviderModelsDrawer = ({
  visible,
  provider,
  onClose,
  onAddModel,
  onEditModel,
  onDeleteModel,
  onToggleModel,
  togglingModelId,
  deletingModelId,
}) => {
  const models = provider?.models || [];

  const columns = [
    {
      title: "Model ID",
      dataIndex: "model_id",
      key: "model_id",
      render: (val) => <Text code style={{ fontSize: 12 }}>{val}</Text>,
    },
    {
      title: "Display Name",
      dataIndex: "display_name",
      key: "display_name",
      render: (val) => val || <Text type="secondary">—</Text>,
    },
    {
      title: "Input $/1M",
      dataIndex: "input_cost_per_million",
      key: "input_cost_per_million",
      width: 110,
      align: "right",
      render: formatCost,
    },
    {
      title: "Output $/1M",
      dataIndex: "output_cost_per_million",
      key: "output_cost_per_million",
      width: 110,
      align: "right",
      render: formatCost,
    },
    {
      title: "Enabled",
      dataIndex: "enabled",
      key: "enabled",
      width: 90,
      align: "center",
      render: (val, record) => (
        <Switch
          checked={val}
          size="small"
          loading={togglingModelId === record.model_id}
          onChange={(checked) => onToggleModel(record, checked)}
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 110,
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="Edit model">
            <Button
              icon={<EditOutlined />}
              size="small"
              onClick={() => onEditModel(record)}
            />
          </Tooltip>
          <Popconfirm
            title={`Remove "${record.model_id}"?`}
            description="The model is removed from this provider's catalog."
            okText="Remove"
            okType="danger"
            cancelText="Cancel"
            onConfirm={() => onDeleteModel(record)}
          >
            <Tooltip title="Remove model">
              <Button
                danger
                icon={<DeleteOutlined />}
                size="small"
                loading={deletingModelId === record.model_id}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Drawer
      title={
        <Space>
          <span>Manage Models</span>
          {provider && (
            <>
              <Tag style={{ textTransform: "lowercase" }}>{provider.provider_key}</Tag>
              <Text type="secondary" style={{ fontSize: 13 }}>{provider.name}</Text>
            </>
          )}
        </Space>
      }
      open={visible}
      onClose={onClose}
      width={920}
      destroyOnClose
      extra={
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={onAddModel}
          disabled={!provider}
        >
          Add Model
        </Button>
      }
    >
      {provider && (
        <Descriptions
          size="small"
          column={3}
          bordered
          style={{ marginBottom: 16 }}
        >
          <Descriptions.Item label="API Key">
            {provider.has_api_key ? (
              <Text code style={{ fontSize: 12 }}>{provider.api_key_masked || "••••"}</Text>
            ) : (
              <Text type="danger">not set</Text>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            {provider.enabled ? (
              <Tag color="green">enabled</Tag>
            ) : (
              <Tag color="red">disabled</Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Version">v{provider.version ?? 1}</Descriptions.Item>
        </Descriptions>
      )}

      {models.length === 0 ? (
        <Empty
          description="No models yet — click 'Add Model' to register one."
          style={{ padding: "40px 0" }}
        />
      ) : (
        <Table
          columns={columns}
          dataSource={models}
          rowKey={(r) => r.model_id}
          pagination={false}
          size="middle"
        />
      )}
    </Drawer>
  );
};

export default ProviderModelsDrawer;
